import React, { useState, useRef, KeyboardEvent, useEffect, useMemo } from 'react';
import { ArrowUp, Paperclip, Atom, Search, Zap, Microscope, X, FileText, Image, File as FileIcon, Square, FolderOpen, HardDrive, MessageSquare, Cloud, Video, Headphones, Puzzle } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { ThinkingMode } from '../types';
import { InstalledSkill } from '../services/workflowApi';

// @ 提及文件项
export type MentionSource = 'case' | 'local' | 'slack' | 'gdrive' | 'onedrive';

export interface MentionItem {
  id: string;
  name: string;
  source: MentionSource;      // 来源：案件 / 本地 / Slack / GDrive / OneDrive
  folderName: string;         // 所属文件夹名 (频道名 / Drive文件夹名)
}

// 文件上传状态
export interface FileUploadState {
  file: File;
  progress: number;
  status: 'uploading' | 'parsing' | 'done' | 'error';
  extractedText?: string;
  error?: string;
}

interface ChatInputProps {
  onSendMessage: (message: string, pendingFiles?: File[], thinkingMode?: ThinkingMode, mentions?: MentionItem[], isToolResponse?: boolean, forcedSkillId?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  currentLanguage: string;
  fileUploadState?: FileUploadState | null;
  onFileSelect?: (file: File) => void;
  onFileRemove?: () => void;
  onLegalDrafterOpen?: () => void;

  // 加载和取消状态
  isLoading?: boolean;
  onCancelRequest?: () => void;

  // 隐藏深度思考按钮
  hideThinkingButton?: boolean;

  // 受控输入模式 - 用于状态持久化
  message?: string;
  onMessageChange?: (message: string) => void;

  // @ 提及文件列表
  mentionFiles?: MentionItem[];

  // 🧩 Architecture 3.0: 已安装的 SubAgent 技能列表
  installedSkills?: InstalledSkill[];
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder,
  currentLanguage,
  fileUploadState,
  onFileSelect,
  onFileRemove,
  onLegalDrafterOpen,

  isLoading = false,
  onCancelRequest,

  hideThinkingButton = false,

  // 受控输入模式
  message: controlledMessage,
  onMessageChange,

  // @ 提及
  mentionFiles = [],

  // 🧩 Architecture 3.0: SubAgent 技能列表
  installedSkills = [],
}) => {
  // 非受控模式的本地状态
  const [uncontrolledMessage, setUncontrolledMessage] = useState('');

  // 判断是否为受控模式
  const isControlled = controlledMessage !== undefined && onMessageChange !== undefined;
  const message = isControlled ? controlledMessage : uncontrolledMessage;
  const setMessage = isControlled ? onMessageChange : setUncontrolledMessage;

  const [showThinkingMenu, setShowThinkingMenu] = useState(false);
  const [selectedThinking, setSelectedThinking] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // @ 提及状态
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0); // @ 符号在 message 中的位置
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState<MentionItem[]>([]);
  const mentionMenuRef = useRef<HTMLDivElement>(null);

  // 🧩 Architecture 3.0: / Slash Command 状态
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashCursorPos, setSlashCursorPos] = useState(0);
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
  const [forcedSkill, setForcedSkill] = useState<{ id: string; name: string } | null>(null);
  const [showToolboxMenu, setShowToolboxMenu] = useState(false);
  const slashMenuRef = useRef<HTMLDivElement>(null);
  const toolboxMenuRef = useRef<HTMLDivElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(currentLanguage);

  // 使用翻译系统获取占位符
  const translatedPlaceholder = placeholder || t('typeMessage');

  const handleSubmit = () => {
    // Check if we can submit: either have text message or have uploaded files or mentioned items
    const canSubmit = (message.trim() || uploadedFiles.length > 0 || selectedMentions.length > 0) && !disabled;

    if (canSubmit) {
      // 如果有文件，传递所有上传的文件
      const pendingFiles = uploadedFiles.length > 0 ? uploadedFiles : undefined;

      // 构造最终的消息，包括文本和引用的标签
      let messageContent = message.trim();
      if (selectedMentions.length > 0) {
        const mentionStrings = selectedMentions.map(m => `@[${m.folderName}/${m.name}]`).join(' ');
        messageContent = messageContent ? `${messageContent}\n\n${mentionStrings}` : mentionStrings;
      }
      
      const finalMessage = messageContent || (pendingFiles ? '请总结这些文件的主要内容' : '');

      // 传递思维模式、提及其它文件、以及 🧩 绿色通道技能 ID
      onSendMessage(finalMessage, pendingFiles, selectedThinking as ThinkingMode, selectedMentions, false, forcedSkill?.id);
      setMessage('');
      setForcedSkill(null); // 发送后清除技能锁定
      setUploadedFiles([]);
      setSelectedMentions([]);
      // 发送后清除思维模式选择
      setSelectedThinking(null);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // @ 提及菜单打开时拦截方向键和回车
    if (showMentionMenu && filteredMentionFiles.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex(prev => Math.min(prev + 1, filteredMentionFiles.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleMentionSelect(filteredMentionFiles[mentionSelectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionMenu(false);
        return;
      }
    }

    // 🧩 / Slash 菜单打开时拦截方向键和回车
    if (showSlashMenu && filteredSlashSkills.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashSelectedIndex(prev => Math.min(prev + 1, filteredSlashSkills.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSlashSelect(filteredSlashSkills[slashSelectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSlashMenu(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // 过滤 @ 提及列表
  const filteredMentionFiles = useMemo(() => {
    if (!showMentionMenu || mentionFiles.length === 0) return [];
    if (!mentionQuery) return mentionFiles.slice(0, 8);
    const q = mentionQuery.toLowerCase();
    return mentionFiles.filter(
      f => f.name.toLowerCase().includes(q) || f.folderName.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [showMentionMenu, mentionQuery, mentionFiles]);

  // 🧩 过滤 / Slash 技能列表
  const filteredSlashSkills = useMemo(() => {
    if (!showSlashMenu || installedSkills.length === 0) return [];
    if (!slashQuery) return installedSkills.slice(0, 8);
    const q = slashQuery.toLowerCase();
    return installedSkills.filter(
      s => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [showSlashMenu, slashQuery, installedSkills]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setMessage(val);

    // 检测 @ 提及
    if (mentionFiles.length > 0) {
      // 向前查找最近的 @
      const textBeforeCursor = val.slice(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf('@');

      if (atIndex >= 0) {
        // @ 前面必须是空字符或行首
        const charBeforeAt = atIndex > 0 ? val[atIndex - 1] : ' ';
        if (charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) {
          const queryStr = textBeforeCursor.slice(atIndex + 1);
          // 如果查询串中有空格或换行则视为结束
          if (!queryStr.includes(' ') && !queryStr.includes('\n')) {
            setMentionQuery(queryStr);
            setMentionCursorPos(atIndex);
            setShowMentionMenu(true);
            setMentionSelectedIndex(0);
          } else {
            setShowMentionMenu(false);
          }
        } else {
          setShowMentionMenu(false);
        }
      } else {
        setShowMentionMenu(false);
      }
    }

    // 🧩 检测 / Slash Command（与 @ 检测逻辑完全对等）
    if (installedSkills.length > 0) {
      const textBeforeCursor2 = val.slice(0, cursorPos);
      const slashIndex = textBeforeCursor2.lastIndexOf('/');

      if (slashIndex >= 0) {
        const charBeforeSlash = slashIndex > 0 ? val[slashIndex - 1] : ' ';
        if (charBeforeSlash === ' ' || charBeforeSlash === '\n' || slashIndex === 0) {
          const queryStr = textBeforeCursor2.slice(slashIndex + 1);
          if (!queryStr.includes(' ') && !queryStr.includes('\n')) {
            setSlashQuery(queryStr);
            setSlashCursorPos(slashIndex);
            setShowSlashMenu(true);
            setSlashSelectedIndex(0);
          } else {
            setShowSlashMenu(false);
          }
        } else {
          setShowSlashMenu(false);
        }
      } else {
        setShowSlashMenu(false);
      }
    }

    // 自动调整高度
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
  };

  // 选择 @ 提及的文件
  const handleMentionSelect = (item: MentionItem) => {
    // 移除用户输入的 @query
    const before = message.slice(0, mentionCursorPos);
    const afterCursor = message.slice(mentionCursorPos + 1 + mentionQuery.length);
    const newMessage = before + afterCursor;
    setMessage(newMessage);
    
    // 添加到引用的标签列表中
    if (!selectedMentions.find(m => m.id === item.id)) {
      setSelectedMentions(prev => [...prev, item]);
    }
    
    setShowMentionMenu(false);
    setMentionQuery('');

    // 恢复焦点并设置光标位置
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const pos = before.length;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };
  
  const handleMentionRemove = (index: number) => {
    setSelectedMentions(prev => prev.filter((_, i) => i !== index));
  };

  // 🧩 选择 / Slash 技能
  const handleSlashSelect = (skill: InstalledSkill) => {
    // 移除用户输入的 /query
    const before = message.slice(0, slashCursorPos);
    const afterCursor = message.slice(slashCursorPos + 1 + slashQuery.length);
    const newMessage = before + afterCursor;
    setMessage(newMessage);

    // 锁定技能
    setForcedSkill({ id: skill.id, name: skill.name });
    setShowSlashMenu(false);
    setSlashQuery('');

    // 恢复焦点
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const pos = before.length;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  // 🧩 从 Toolbox 弹窗选择技能
  const handleToolboxSelect = (skill: InstalledSkill) => {
    setForcedSkill({ id: skill.id, name: skill.name });
    setShowToolboxMenu(false);
    textareaRef.current?.focus();
  };

  // 🧩 关闭 Toolbox 点击外部
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolboxMenuRef.current && !toolboxMenuRef.current.contains(event.target as Node)) {
        setShowToolboxMenu(false);
      }
    };
    if (showToolboxMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showToolboxMenu]);

  const handleThinkingSelect = (thinking: string) => {
    setShowThinkingMenu(false);

    // 设置选择的思考模式
    const selectedOption = thinkingOptions.find(option => option.text === thinking);
    setSelectedThinking(selectedOption ? selectedOption.key : null);

    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedThinking(null);
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadedFiles(prev => [...prev, ...files]);
    // 清空input值，允许重复上传同一文件
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleFileRemove = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 拖拽事件处理
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // 只有当离开容器时才取消拖拽状态
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // 首先检查是否有 application/json 类型的引用数据
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        const parsed = JSON.parse(jsonData);
        if (parsed.type === 'mention' && parsed.item) {
          if (!selectedMentions.find(m => m.id === parsed.item.id)) {
            setSelectedMentions(prev => [...prev, parsed.item as MentionItem]);
          }
          return;
        }
      }
    } catch(err) {
      // 忽略解析错误继续处理其他类型
    }

    // 后备方案检查是否有 @ 引用文本内容 (旧的 text/plain 方式)
    const textData = e.dataTransfer.getData('text/plain');
    if (textData && textData.startsWith('@[')) {
      // We will parse it and add it as a mention item
      const match = textData.match(/^@\[([^/]+)\/(.+)\]$/);
      if (match) {
        const item: MentionItem = {
          id: match[1] + match[2],
          source: 'local', // Placeholder source
          folderName: match[1],
          name: match[2]
        };
        if (!selectedMentions.find(m => m.name === item.name)) {
          setSelectedMentions(prev => [...prev, item]);
        }
      }
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
    }
  };

  const getFileIcon = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return FileText;
      case 'doc':
      case 'docx':
        return FileIcon;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
        return Image;
      case 'mp4':
      case 'webm':
      case 'mov':
      case 'avi':
      case 'mkv':
        return Video;
      case 'mp3':
      case 'wav':
      case 'm4a':
      case 'ogg':
        return Headphones;
      default:
        return FileIcon;
    }
  };

  const getTagStyle = (source: string, folderName: string) => {
    // 动态判断各类型的标签颜色
    let s = source.toLowerCase();
    if (s === 'local' || folderName.toLowerCase() === 'local') {
      return {
        bgClass: 'bg-slate-50 hover:bg-slate-100',
        borderClass: 'border-slate-200 hover:border-slate-300',
        textPrimary: 'text-slate-700',
        textSecondary: 'text-slate-500',
        iconClass: 'text-slate-500',
        deleteHover: 'hover:bg-slate-500'
      };
    }
    if (s === 'case' || folderName.toLowerCase() === 'case') {
      return {
        bgClass: 'bg-amber-50 hover:bg-amber-100',
        borderClass: 'border-amber-200 hover:border-amber-300',
        textPrimary: 'text-amber-800',
        textSecondary: 'text-amber-600',
        iconClass: 'text-amber-500',
        deleteHover: 'hover:bg-amber-500'
      };
    }
    if (s === 'gdrive' || folderName.toLowerCase().includes('gdrive')) {
      return {
        bgClass: 'bg-emerald-50 hover:bg-emerald-100',
        borderClass: 'border-emerald-200 hover:border-emerald-300',
        textPrimary: 'text-emerald-800',
        textSecondary: 'text-emerald-600',
        iconClass: 'text-emerald-500',
        deleteHover: 'hover:bg-emerald-500'
      };
    }
    if (s === 'onedrive' || folderName.toLowerCase().includes('onedrive')) {
      return {
        bgClass: 'bg-blue-50 hover:bg-blue-100',
        borderClass: 'border-blue-200 hover:border-blue-300',
        textPrimary: 'text-blue-800',
        textSecondary: 'text-blue-600',
        iconClass: 'text-blue-500',
        deleteHover: 'hover:bg-blue-500'
      };
    }
    if (s === 'slack' || folderName.toLowerCase().includes('slack')) {
      return {
        bgClass: 'bg-fuchsia-50 hover:bg-fuchsia-100',
        borderClass: 'border-fuchsia-200 hover:border-fuchsia-300',
        textPrimary: 'text-fuchsia-800',
        textSecondary: 'text-fuchsia-600',
        iconClass: 'text-fuchsia-500',
        deleteHover: 'hover:bg-fuchsia-500'
      };
    }
    if (folderName.toLowerCase().includes('outlook')) {
      return {
        bgClass: 'bg-sky-50 hover:bg-sky-100',
        borderClass: 'border-sky-200 hover:border-sky-300',
        textPrimary: 'text-sky-800',
        textSecondary: 'text-sky-600',
        iconClass: 'text-sky-500',
        deleteHover: 'hover:bg-sky-500'
      };
    }
    if (folderName.toLowerCase().includes('gmail')) {
      return {
        bgClass: 'bg-red-50 hover:bg-red-100',
        borderClass: 'border-red-200 hover:border-red-300',
        textPrimary: 'text-red-800',
        textSecondary: 'text-red-500',
        iconClass: 'text-red-500',
        deleteHover: 'hover:bg-red-500'
      };
    }
    // Default fallback
    return {
      bgClass: 'bg-indigo-50 hover:bg-indigo-100',
      borderClass: 'border-indigo-200 hover:border-indigo-300',
      textPrimary: 'text-indigo-800',
      textSecondary: 'text-indigo-500',
      iconClass: 'text-indigo-500',
      deleteHover: 'hover:bg-indigo-500'
    };
  };

  const thinkingOptions = [
    {
      key: 'standard',
      icon: Search,
      text: t('deepInsight'),
      color: "text-gray-600",
      subtitle: t('deepInsightDesc'),
      displayIcon: Search
    },
    {
      key: 'deep',
      icon: Zap,
      text: t('deepExploration'),
      color: "text-gray-600",
      subtitle: t('deepExplorationDesc'),
      displayIcon: Zap
    },
    {
      key: 'analyze',
      icon: Microscope,
      text: t('deepDeconstruction'),
      color: "text-gray-600",
      subtitle: t('deepDeconstructionDesc'),
      displayIcon: Microscope
    },
  ];


  // 获取当前选择的图标
  const getCurrentIcon = () => {
    const selectedOption = thinkingOptions.find(option => option.key === selectedThinking);
    return selectedOption ? selectedOption.displayIcon : Atom;
  };

  // 获取当前按钮样式
  const getButtonStyle = () => {
    return selectedThinking
      ? "flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md transition-colors"
      : "flex items-center justify-center w-8 h-8 border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors";
  };

  const getButtonBackgroundColor = () => {
    return selectedThinking ? '#E1E1E0' : 'white';
  };

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowThinkingMenu(false);
      }
      if (mentionMenuRef.current && !mentionMenuRef.current.contains(event.target as Node)) {
        setShowMentionMenu(false);
      }
    };

    if (showThinkingMenu || showMentionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showThinkingMenu, showMentionMenu]);

  return (
    <div
      className="bg-ai-bubble p-3"
      ref={containerRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="max-w-2xl mx-auto">
        {/* 输入框容器 */}
        <div className={`relative border rounded-lg bg-white shadow-sm transition-all ${isDragging ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-200'}`}>
          {/* 拖拽提示覆盖层 */}
          {isDragging && (
            <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 rounded-lg z-10">
              <div className="text-center">
                <div className="text-3xl mb-2">📁</div>
                <p className="text-blue-600 font-medium">松开以上传文件</p>
                <p className="text-blue-400 text-sm">支持 PDF、DOC、图片等</p>
              </div>
            </div>
          )}
          {/* 输入框 */}
          <div className="p-2">
            {/* 引用标签和已上传文件显示 */}
            {(uploadedFiles.length > 0 || selectedMentions.length > 0 || forcedSkill) && (
              <div className="flex flex-wrap gap-2 mb-2">
                {/* 🧩 锁定的技能胶囊标签 */}
                {forcedSkill && (
                  <div className="relative group" title={`Skill: ${forcedSkill.name}`}>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border shadow-sm transition-all duration-200 cursor-default bg-purple-50 border-purple-200">
                      <Puzzle size={14} className="text-purple-500 flex-shrink-0" />
                      <span className="text-xs font-semibold text-purple-700 truncate max-w-[140px]">
                        {forcedSkill.name}
                      </span>
                      <button
                        onClick={(e) => { e.preventDefault(); setForcedSkill(null); }}
                        className="ml-1 w-4 h-4 text-purple-400 hover:text-white hover:bg-purple-500 rounded-full flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                        title="取消技能锁定"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  </div>
                )}
                {/* 渲染引用文件签（正式样式） */}
                {selectedMentions.map((item, index) => {
                  const style = getTagStyle(item.source, item.folderName);
                  return (
                    <div
                      key={`mention-${index}-${item.id}`}
                      className="relative group"
                      title={`${item.folderName}/${item.name}`}
                    >
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border shadow-sm transition-all duration-200 cursor-default ${style.bgClass} ${style.borderClass}`}>
                        <div className={`flex items-center justify-center flex-shrink-0 ${style.iconClass}`}>
                          {item.folderName.includes('Gmail') || item.folderName.includes('Outlook') ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          ) : (
                            <FileIcon size={14} className="currentColor" />
                          )}
                        </div>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className={`text-[10px] font-medium capitalize whitespace-nowrap hidden sm:inline-block ${style.textSecondary}`}>
                            {item.source}
                          </span>
                          <span className={`text-xs font-semibold truncate max-w-[140px] ${style.textPrimary}`}>
                            {item.name}
                          </span>
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); handleMentionRemove(index); }}
                          className={`ml-1 w-4 h-4 text-gray-400 hover:text-white rounded-full flex items-center justify-center transition-colors duration-200 flex-shrink-0 ${style.deleteHover}`}
                          title="移除"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* 渲染上传文件 */}
                {uploadedFiles.map((file, index) => {
                  const IconComponent = getFileIcon(file);
                  return (
                    <div
                      key={`upload-${index}`}
                      className="relative group"
                      title={file.name}
                    >
                      <div className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] rounded-md border border-[#E5E5E5] shadow-sm hover:shadow-md hover:border-[#CCCCCC] hover:bg-[#F5F5F5] transition-all duration-200 cursor-default">
                        <div className="w-8 h-8 rounded bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 shadow-sm">
                          <IconComponent size={16} className="text-white" />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                            {file.name}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); handleFileRemove(index); }}
                          className="ml-1 w-5 h-5 bg-gray-400/80 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                          title={t('deleteFile')}
                        >
                          <X size={10} className="text-white" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* @ 提及下拉菜单 */}
            {showMentionMenu && filteredMentionFiles.length > 0 && (
              <div
                ref={mentionMenuRef}
                className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto z-20"
              >
                <div className="px-3 py-1.5 text-xs text-gray-400 font-medium uppercase tracking-wider border-b border-gray-100">
                  {t('mentionFiles') || 'Reference a file'}
                </div>
                {filteredMentionFiles.map((item, index) => (
                  <button
                    key={`${item.source}-${item.id}`}
                    onClick={() => handleMentionSelect(item)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                      index === mentionSelectedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                  >
                    {item.source === 'case' ? (
                      <FolderOpen size={14} className="text-amber-500 flex-shrink-0" />
                    ) : item.source === 'gdrive' ? (
                      <Cloud size={14} className="text-green-500 flex-shrink-0" />
                    ) : item.source === 'onedrive' ? (
                      <Cloud size={14} className="text-sky-500 flex-shrink-0" />
                    ) : item.source === 'slack' ? (
                      <MessageSquare size={14} className="text-purple-500 flex-shrink-0" />
                    ) : (
                      <HardDrive size={14} className="text-blue-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 truncate">{item.name}</div>
                      <div className="text-xs text-gray-400 truncate">{item.folderName}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* 🧩 / Slash Command 下拉菜单 */}
            {showSlashMenu && filteredSlashSkills.length > 0 && (
              <div
                ref={slashMenuRef}
                className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto z-20"
              >
                <div className="px-3 py-1.5 text-xs text-gray-400 font-medium uppercase tracking-wider border-b border-gray-100">
                  🧩 Select an Agent
                </div>
                {filteredSlashSkills.map((skill, index) => (
                  <button
                    key={skill.id}
                    onClick={() => handleSlashSelect(skill)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                      index === slashSelectedIndex ? 'bg-purple-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Puzzle size={14} className="text-purple-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 truncate">{skill.name}</div>
                      <div className="text-xs text-gray-400 truncate">{skill.description}</div>
                    </div>
                    {skill.pricing_model !== 'free' && (
                      <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                        {skill.pricing_model === 'subscription' ? 'PRO' : `$${skill.price}`}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={translatedPlaceholder}
              className="w-full border-0 resize-none focus:outline-none bg-transparent text-sm"
              style={{ minHeight: '32px', maxHeight: '100px' }}
              rows={1}
            />
          </div>

          {/* 按钮行 */}
          <div className="flex items-center justify-between px-3 pb-2">
            <div className="flex items-center gap-2">
              {/* 思考按钮 - 可通过 hideThinkingButton 隐藏 */}
              {!hideThinkingButton && (
                <div ref={menuRef}>
                  <div className="relative">
                    <button
                      onClick={() => setShowThinkingMenu(!showThinkingMenu)}
                      className={getButtonStyle()}
                      style={{ backgroundColor: getButtonBackgroundColor() }}
                      title={t('thinkingOptions')}
                      onMouseEnter={(e) => {
                        if (!selectedThinking) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getButtonBackgroundColor();
                      }}
                    >
                      {React.createElement(getCurrentIcon(), { size: 16, className: "text-gray-600" })}

                      {/* 清除选择的小叉号 */}
                      {selectedThinking && (
                        <button
                          onClick={handleClearSelection}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-gray-500 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
                          title={t('clearSelection')}
                        >
                          <X size={10} className="text-white" />
                        </button>
                      )}
                    </button>

                    {/* 下拉菜单 */}
                    {showThinkingMenu && (
                      <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-64 z-10">
                        {thinkingOptions.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleThinkingSelect(option.text)}
                            className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <option.icon size={18} className={`${option.color} mt-0.5`} />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-800">{option.text}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{option.subtitle}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 🧩 Architecture 3.0: Toolbox 按钮 */}
              {installedSkills.length > 0 && (
                <div ref={toolboxMenuRef} className="relative">
                  <button
                    onClick={() => setShowToolboxMenu(!showToolboxMenu)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md border transition-colors ${
                      forcedSkill
                        ? 'bg-purple-50 border-purple-200 text-purple-600'
                        : 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200'
                    }`}
                    title="🧩 Select Agent"
                  >
                    <Puzzle size={16} />
                  </button>

                  {/* Toolbox 弹窗菜单 */}
                  {showToolboxMenu && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-64 z-10 max-h-64 overflow-y-auto">
                      <div className="px-3 py-1.5 text-xs text-gray-400 font-medium uppercase tracking-wider border-b border-gray-100">
                        🧩 Installed Agents
                      </div>
                      {forcedSkill && (
                        <button
                          onClick={() => { setForcedSkill(null); setShowToolboxMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-red-50 transition-colors border-b border-gray-100"
                        >
                          <X size={14} className="text-red-400" />
                          <span className="text-sm text-red-500">Clear Selection</span>
                        </button>
                      )}
                      {installedSkills.map((skill) => (
                        <button
                          key={skill.id}
                          onClick={() => handleToolboxSelect(skill)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            forcedSkill?.id === skill.id ? 'bg-purple-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <Puzzle size={16} className={forcedSkill?.id === skill.id ? 'text-purple-500' : 'text-gray-400'} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">{skill.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5 truncate">{skill.description}</div>
                          </div>
                          {forcedSkill?.id === skill.id && (
                            <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-medium">ACTIVE</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 附件按钮 */}
              <button
                onClick={handleFileUpload}
                className={`w-8 h-8 flex items-center justify-center rounded-md border transition-colors ${uploadedFiles.length > 0
                  ? 'text-gray-600'
                  : 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200'
                  }`}
                style={uploadedFiles.length > 0 ? {
                  backgroundColor: '#E1E1E0',
                  borderColor: '#E1E1E0'
                } : {
                  borderColor: '#e5e7eb'
                }}
                title={t('addAttachment') || 'Add Attachment'}
              >
                <Paperclip size={16} className="text-gray-600" />
              </button>

              {/* 隐藏的文件输入 */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.txt,.xlsx,.pptx,.mp3,.wav,.m4a,.ogg,.mp4,.webm,.mov,.avi,.mkv"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* 发送/取消按钮 */}
            {isLoading ? (
              <button
                onClick={onCancelRequest}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 transition-colors bg-gray-100 hover:bg-gray-200"
                title={t('cancelRequest') || 'Cancel Request'}
              >
                <Square size={12} fill="#dc2626" className="text-red-600" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={(!message.trim() && uploadedFiles.length === 0) || disabled}
                className={`w-8 h-8 flex items-center justify-center rounded border transition-colors ${(!message.trim() && uploadedFiles.length === 0) || disabled
                  ? 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200 cursor-not-allowed'
                  : 'text-gray-600'
                  }`}
                style={(message.trim() || uploadedFiles.length > 0) && !disabled ? {
                  backgroundColor: '#E1E1E0',
                  borderColor: '#E1E1E0'
                } : {
                  borderColor: '#e5e7eb'
                }}
                title={t('sendMessage') || 'Send Message'}
              >
                <ArrowUp size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInput; 